import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { IProfile } from '@/types'
import { fetchProfile } from '@/services/fetch-profile'
import { QUERY_KEYS } from '@/constants/queryKeys'
import { isIdentifiedUser } from '@/constants/roles'
import { isTokenVerificationFailure } from '@/utils/errors/sessionAuthFailure'

export interface UseUserResult {
  profile: IProfile | undefined
  isLoading: boolean
  isLoggedIn: boolean
  showScores: boolean
  refetch: () => Promise<unknown>
  error: Error | null
}

export const useUser = (): UseUserResult => {
  const { data, isLoading, refetch, error } = useQuery({
    queryKey: QUERY_KEYS.profile,
    queryFn: () => fetchProfile(),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    // A token the auth service could not verify fails the same way on a retry;
    // the session end (or not) is decided once, in the QueryCache handler.
    retry: (failureCount, error) => failureCount < 1 && !isTokenVerificationFailure(error),
    retryDelay: 1000,
  })

  const userQueryResult = useMemo(() => {
    return {
      profile: data,
      // In React Query v5, isPending is true whenever status==='pending' (including idle resets).
      // Using only isLoading (status==='pending' && fetchStatus==='fetching') ensures we only
      // block while actively fetching — not while the query is reset-but-not-yet-fetching.
      isLoading: isLoading,
      isLoggedIn: isIdentifiedUser(data?.rbac?.role),
      showScores: !isLoading && !(data?.settings?.hideScores ?? false),
      refetch: refetch,
      error: error,
    }
  }, [data, isLoading, refetch, error])

  return userQueryResult
}